import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Paperclip, Plus, Send, FileText, Image as ImageIcon, X, Lightbulb, Eye } from "lucide-react";

const STATUS = {
  new: "bg-sky-100 text-sky-800", reviewing: "bg-amber-100 text-amber-800", planned: "bg-violet-100 text-violet-800",
  in_progress: "bg-blue-100 text-blue-800", done: "bg-emerald-100 text-emerald-800", declined: "bg-slate-100 text-slate-600",
};
const STATUS_LABEL = { new: "New", reviewing: "Reviewing", planned: "Planned", in_progress: "In progress", done: "Done", declined: "Declined" };
const KINDS = { feature: "New feature", change: "Change request", bug: "Bug / problem", question: "Question" };
const fmtWhen = (iso) => new Date(iso).toLocaleString("en-NZ", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

export function useAttachmentUpload() {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const add = async (list) => {
    setUploading(true);
    try {
      for (const f of Array.from(list || [])) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/requests/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((prev) => [...prev, data]);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t("Upload failed"));
    } finally {
      setUploading(false);
    }
  };
  const remove = (id) => setFiles((p) => p.filter((f) => f.id !== id));
  const reset = () => setFiles([]);
  return { files, uploading, add, remove, reset };
}

export function AttachmentChips({ files, onRemove }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="attachment-chips">
      {files.map((f) => (
        <span key={f.id} className="inline-flex items-center gap-1">
          <span className="inline-flex items-center gap-1 rounded-full border bg-muted/40 px-2 py-0.5 text-xs" data-testid={`attachment-${f.id}`}>
            {f.content_type?.startsWith("image/") ? <ImageIcon className="h-3 w-3" /> : <FileText className="h-3 w-3" />}
            <span className="max-w-[12rem] truncate">{f.name}</span>
            {onRemove && <button type="button" onClick={(e) => { e.preventDefault(); onRemove(f.id); }} className="ml-0.5"><X className="h-3 w-3" /></button>}
          </span>
          <a href={`${API}/requests/attachments/${f.id}`} target="_blank" rel="noreferrer" title="View in a new tab"
            className="inline-flex items-center gap-0.5 text-xs text-primary underline-offset-2 hover:underline" data-testid={`attachment-view-${f.id}`}>
            <Eye className="h-3 w-3" /> View
          </a>
        </span>
      ))}
    </div>
  );
}

export function AttachButton({ up }) {
  const { t } = useTranslation();
  const ref = useRef(null);
  return (
    <>
      <input ref={ref} type="file" multiple accept="image/*,.pdf,.csv,.txt,.xls,.xlsx,.doc,.docx" className="hidden" onChange={(e) => { up.add(e.target.files); e.target.value = ""; }} data-testid="attachment-input" />
      <Button type="button" variant="outline" size="sm" onClick={() => ref.current?.click()} disabled={up.uploading} className="gap-1.5" data-testid="attach-btn">
        {up.uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />} {t("Attach screenshot / file")}
      </Button>
    </>
  );
}

export function NewRequestDialog({ open, onOpenChange }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState("feature");
  const [priority, setPriority] = useState("normal");
  const [body, setBody] = useState("");
  const up = useAttachmentUpload();
  const create = useMutation({
    mutationFn: () => api.post("/requests", { title, kind, priority, body, attachment_ids: up.files.map((f) => f.id) }),
    onSuccess: () => {
      toast.success(t("Request sent — we'll reply here in the app."));
      qc.invalidateQueries({ queryKey: ["requests"] });
      setTitle(""); setBody(""); setKind("feature"); setPriority("normal"); up.reset();
      onOpenChange(false);
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="new-request-dialog">
        <DialogHeader>
          <DialogTitle>{t("Request a change or feature")}</DialogTitle>
          <DialogDescription>{t("Tell us exactly what you need. Add screenshots, PDFs or CSV examples so the developer can build it to suit.")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label>{t("Type")}</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger data-testid="request-kind"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(KINDS).map(([k, v]) => <SelectItem key={k} value={k}>{t(v)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>{t("Priority")}</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="request-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">{t("Low — nice to have")}</SelectItem>
                  <SelectItem value="normal">{t("Normal")}</SelectItem>
                  <SelectItem value="high">{t("High — blocking our work")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label>{t("Title")}</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("e.g. Add a laundry count column to the Daily List")} data-testid="request-title" />
          </div>
          <div className="space-y-1">
            <Label>{t("What do you need, and why?")}</Label>
            <Textarea rows={6} value={body} onChange={(e) => setBody(e.target.value)} placeholder={t("Describe the situation, what should happen, and any rules or examples.")} data-testid="request-body" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <AttachButton up={up} />
            <span className="text-xs text-muted-foreground">{t("Images, PDF, CSV, Word, Excel · max 15 MB each")}</span>
          </div>
          {up.files.length > 0 && <AttachmentChips files={up.files} onRemove={up.remove} />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("Cancel")}</Button>
          <Button onClick={() => create.mutate()} disabled={create.isPending || up.uploading || title.trim().length < 3 || body.trim().length < 3} className="gap-2" data-testid="request-submit">
            {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {t("Send request")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Thread({ req, isAdmin, onClose }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  const [status, setStatus] = useState(req.status);
  const [note, setNote] = useState(req.admin_note || "");
  const [pub, setPub] = useState(!!req.public);
  const [pubTitle, setPubTitle] = useState(req.public_title || "");
  const up = useAttachmentUpload();
  const refresh = () => qc.invalidateQueries({ queryKey: ["requests"] });
  const reply = useMutation({
    mutationFn: () => api.post(`/requests/${req.id}/messages`, { body, attachment_ids: up.files.map((f) => f.id) }),
    onSuccess: () => { setBody(""); up.reset(); refresh(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const setSt = useMutation({
    mutationFn: () => api.patch(`/requests/${req.id}/status`, { status, admin_note: note, public: pub, public_title: pubTitle }),
    onSuccess: () => { toast.success(t("Status updated")); refresh(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="request-thread">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            {req.title}
            <Badge className={`font-normal ${STATUS[req.status]}`}>{t(STATUS_LABEL[req.status])}</Badge>
            <Badge variant="outline" className="font-normal">{t(KINDS[req.kind] || req.kind)}</Badge>
          </DialogTitle>
          <DialogDescription>
            {isAdmin && <>{req.motel_name} ({req.property_code}) · {req.subscription_status} · </>}
            {req.requester_name} · {fmtWhen(req.created_at)}
          </DialogDescription>
        </DialogHeader>
        {req.admin_note && (
          <div className="rounded-md border border-violet-200 bg-violet-50 p-2.5 text-sm" data-testid="request-admin-note">
            <span className="font-semibold">{t("Developer note")}: </span>{req.admin_note}
          </div>
        )}
        <div className="max-h-[45vh] space-y-3 overflow-y-auto pr-1">
          {req.messages.map((m) => (
            <div key={m.id} className={`rounded-lg p-3 text-sm ${m.by_role === "super_admin" ? "ml-8 bg-violet-50" : "mr-8 bg-muted/50"}`} data-testid={`request-msg-${m.id}`}>
              <div className="mb-1 text-xs text-muted-foreground">{m.by_role === "super_admin" ? t("Developer") : m.by_name} · {fmtWhen(m.at)}</div>
              <div className="whitespace-pre-wrap">{m.body}</div>
              {m.attachments?.length > 0 && <div className="mt-2"><AttachmentChips files={m.attachments} /></div>}
            </div>
          ))}
        </div>
        <div className="space-y-2 border-t pt-3">
          <Textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder={t("Reply or add more detail…")} data-testid="request-reply-body" />
          {up.files.length > 0 && <AttachmentChips files={up.files} onRemove={up.remove} />}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <AttachButton up={up} />
            <Button size="sm" onClick={() => reply.mutate()} disabled={reply.isPending || up.uploading || !body.trim()} className="gap-1.5" data-testid="request-reply-send">
              {reply.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {t("Send")}
            </Button>
          </div>
        </div>
        {isAdmin && (
          <div className="grid gap-2 rounded-md border p-3 sm:grid-cols-[10rem_1fr_auto]" data-testid="request-admin-controls">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="request-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Developer note shown to the requester (optional)" data-testid="request-admin-note-input" />
            <Button size="sm" onClick={() => setSt.mutate()} disabled={setSt.isPending} data-testid="request-status-save">Update</Button>
            <label className="flex items-center gap-2 text-xs sm:col-span-3">
              <input type="checkbox" checked={pub} onChange={(e) => setPub(e.target.checked)} data-testid="request-public-toggle" />
              Show on the public roadmap (planned / in progress / shipped only — never shows the customer or thread)
            </label>
            {pub && (
              <Input value={pubTitle} onChange={(e) => setPubTitle(e.target.value)} placeholder={`Public wording (defaults to: ${req.title})`} className="sm:col-span-3" data-testid="request-public-title" />
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function RequestsPanel({ isAdmin = false }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState("open");
  const { data: reqs = [] } = useQuery({ queryKey: ["requests"], queryFn: async () => (await api.get("/requests")).data, refetchInterval: 60000 });
  const shown = reqs.filter((r) => (filter === "open" ? !["done", "declined"].includes(r.status) : filter === "all" ? true : r.status === filter));
  const openThread = async (r) => {
    setActive(r);
    if (isAdmin ? r.unread_admin : r.unread_requester) {
      await api.post(`/requests/${r.id}/read`);
      qc.invalidateQueries({ queryKey: ["requests"] });
    }
  };
  const live = active ? reqs.find((r) => r.id === active.id) || active : null;
  return (
    <div className="space-y-3" data-testid="requests-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          {["open", "done", "all"].map((f) => (
            <Button key={f} size="sm" variant={filter === f ? "default" : "outline"} className="h-7 px-2 text-xs" onClick={() => setFilter(f)} data-testid={`requests-filter-${f}`}>
              {t(f === "open" ? "Open" : f === "done" ? "Done" : "All")}
            </Button>
          ))}
        </div>
        {!isAdmin && (
          <Button size="sm" onClick={() => setOpen(true)} className="gap-1.5" data-testid="new-request-btn"><Plus className="h-4 w-4" /> {t("New request")}</Button>
        )}
      </div>
      {shown.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground" data-testid="requests-empty">
          <Lightbulb className="mx-auto mb-2 h-6 w-6 opacity-60" />
          {isAdmin ? "No requests in this view." : t("No requests yet. Spotted something that would make your day easier? Tell us.")}
        </Card>
      ) : (
        <div className="divide-y rounded-md border">
          {shown.map((r) => {
            const unread = isAdmin ? r.unread_admin : r.unread_requester;
            return (
              <button key={r.id} type="button" onClick={() => openThread(r)} className="flex w-full items-start justify-between gap-3 p-3 text-left hover:bg-muted/40" data-testid={`request-row-${r.id}`}>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    {unread && <span className="h-2 w-2 rounded-full bg-rose-500" data-testid={`request-unread-${r.id}`} />}
                    <span className="truncate font-medium">{r.title}</span>
                    <Badge className={`font-normal ${STATUS[r.status]}`}>{t(STATUS_LABEL[r.status])}</Badge>
                    {r.priority === "high" && <Badge className="bg-rose-100 font-normal text-rose-800">{t("High")}</Badge>}
                  </div>
                  <div className="mt-0.5 truncate text-xs text-muted-foreground">
                    {isAdmin && <>{r.motel_name} · </>}{r.requester_name} · {t(KINDS[r.kind] || r.kind)} · {r.messages.length} {t("messages")} · {fmtWhen(r.updated_at)}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
      <NewRequestDialog open={open} onOpenChange={setOpen} />
      {live && <Thread req={live} isAdmin={isAdmin} onClose={() => setActive(null)} />}
    </div>
  );
}
